import React, { useState } from 'react';
import { Mutation, MutationResult } from 'react-apollo';

import gql from 'graphql-tag';
import { RouteComponentProps, withRouter } from 'react-router';
import MasterView from './MasterView';
import { PPDeviceConfigFile } from '../../../graphql/graphql.schema';

import { ApolloError } from 'apollo-client';
import { getDevicePath } from '../routingHelper';
import { ISnackBarParams } from '../../../stores/userFeedback/snackbarStore';
import { TranslationFunction } from 'i18next';
import { translate, getI18n } from 'react-i18next';
import { DEVICE_INSCRIPTION_FIELD_QUERY } from '../DevicesInscription';
import { safeMobxInject } from '../../../stores/storeInjectionHelpers';
import i18n from '../../../translations/i18n';
import { getErrorsMessageGraphQl, IErrorMessageGraphql } from './../../../utils/graphQl';
import PopupView from './../../../ui/popup/PopupView';

interface IMatchParams {
  pvInstallationId: string;
  customerId: string;
  current: string;
}

export interface IProps extends RouteComponentProps<IMatchParams> {
  t: TranslationFunction;
}

interface IStoresProps {
  resetMstError: () => void;
  initErrorsFromConfigFileResponse: (data: PPDeviceConfigFile) => boolean;
  initErrorsFromGQLError: (error: ApolloError) => void;
  showSnackBar: (params: ISnackBarParams) => void;
  masterTypeInternetConnection?: string;
  getMasterDone: boolean;
  isSimRouterReferenceValid: boolean;
  canUpdateMasterConnectionType: boolean;
  setConstellationMac: (isConstellationMac: boolean) => void;
}

// For apolloCache to work requested value should be the same as DeviceInscriptionQuery

const SAVE = gql`
    mutation editMaster(
        $activationCode: String
        $mac: String
        $pvInstallationId: String!
        $lang: String
    ) {
        updateDeviceMST(
            activationCode: $activationCode
            pvInstallationId: $pvInstallationId
            mac: $mac
            lang: $lang
        ) {
            ${DEVICE_INSCRIPTION_FIELD_QUERY}
        }
    }
`;

const MasterQuery = safeMobxInject<IStoresProps, IProps>(
  allStores => ({
    initErrorsFromConfigFileResponse:
      allStores.inscriptionError.initErrorsFromConfigFileResponse,
    initErrorsFromGQLError:
      allStores.inscriptionError.initErrorsFromApolloError,
    resetMstError: allStores.inscriptionError.reset,
    showSnackBar: allStores.snackBar.showSnackBar,
    getMasterDone: allStores.inscriptionSteps.getMasterDone,
    masterTypeInternetConnection:
      allStores.inscription.masterTypeInternetConnection,
    isSimRouterReferenceValid: allStores.inscription.isSimRouterReferenceValid,
    canUpdateMasterConnectionType:
      allStores.inscription.canUpdateMasterConnectionType,
    setConstellationMac: allStores.inscription.setConstellationMac
  }),
  ({
    match: {
      params: { customerId, pvInstallationId }
    },
    history,
    initErrorsFromConfigFileResponse,
    resetMstError,
    showSnackBar,
    isSimRouterReferenceValid,
    canUpdateMasterConnectionType,
    t,
    setConstellationMac,
  }: IProps & IStoresProps) => {
    const [popupVisibility, setPopupVisibility] = useState(false);
    const onHidePopupEvent = () => {
      setPopupVisibility(false);
    };

    return (
      <Mutation
        mutation={SAVE}
        onCompleted={data => {
          if (!initErrorsFromConfigFileResponse(data.updateDeviceMST)) {
            showSnackBar({
              text: t('Success:updateMaster'),
              type: 'success'
            });
            if (isSimRouterReferenceValid && !canUpdateMasterConnectionType) {
              history.push(getDevicePath({ pvInstallationId, customerId }));
            }
          }
        }}
        onError={(error: any) => {
          const errorsMessage = getErrorsMessageGraphQl(
            error.graphQLErrors
          );

          const isUnprocessableEntityError = errorsMessage.some(
            (error: IErrorMessageGraphql) => error ? error.status === 422 : false
          );

          if (isUnprocessableEntityError) {
            setConstellationMac(true);
            showSnackBar({
              text: t('Errors:constellationDeviceSnackBar'),
              type: 'error'
            });

            setPopupVisibility(true);

          } else {
            showSnackBar({
              text: t('Errors:unknownError'),
              type: 'error'
            });
          }
        }}
      >
        {(saveMaster, { loading }: MutationResult) => (
          <>
            <MasterView
              saveLoading={loading}
              save={(master: { mac: string } | { activationCode: string }) => {
                resetMstError();
                saveMaster({
                  variables: {
                    pvInstallationId,
                    lang: i18n.language,
                    ...master
                  }
                });
              }}
            />
            {popupVisibility && <PopupView 
              title={ t('ConstellationMasterPopup:popupTitle') }
              info={ t('Errors:constellationDevicePopup') }
              buttons={[
                {
                  color: 'secondary',
                  text: t('Common:cancel'),
                  onClick: onHidePopupEvent,
                  type: 'cancel'
                },
                {
                  color: 'primary',
                  text: t('ConstellationMasterPopup:goToConstellationAppBtn'),
                  onClick: () => {
                    window.open(t('ConstellationMasterPopup:constellationAppUrl'), '_blank');
                  }
                }
              ]}
            />}
          </>
        )}
      </Mutation>
    );
  }
);
export default withRouter(translate(['Success', 'Errors', 'Common', 'ConstellationMasterPopup'])(MasterQuery));
